import TripResultItem, { TripResultStatus } from 'Modules/BulkBooking/Components/TripResultItem';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import ListContainer from 'Views/Components/_HumanWritten/List/ListContainer';
import { WizardStepHeader } from '../Shared/WizardStepHeader';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { InvalidBulkBookingErrorDto } from './NoAvailableDto';
import { OverbookingWarningDto } from './OverbookingWarningDto';
import { OverbookingWarningList } from './OverbookingWarningList';

interface InvalidBulkTripsListProps {
	bulkBookingErrors: InvalidBulkBookingErrorDto[];
	/**
	 * How many of the selected trips would survive removing the ones listed here. Zero means there is
	 * nothing left to book, so proceeding is not offered — the request would be rejected outright.
	 */
	remainingTripCount: number;
	onRemoveAndProceed: () => void;
	/**
	 * Back to the bulk search criteria, staying in bulk booking mode. Distinct from `onCancel`, which
	 * leaves bulk mode entirely.
	 */
	onChooseDifferentTrips: () => void;
	onCancel: () => void;
}

interface TripData {
	id: string;
	date: Date;
	time: string;
	status: TripResultStatus;
	errorMessage?: string;
	overbookingWarnings?: OverbookingWarningDto[];
}

/**
 * The status shown against a refused trip.
 *
 * Every row used to read `Sold Out`, whatever the reason — so a trip that had been closed, or made
 * private, was reported as full. Only a capacity failure is actually sold out; that is the one case
 * with no reason code, because the warnings describe it instead.
 */
const statusForReason = (reasonCode?: InvalidBulkBookingErrorDto['reasonCode']): TripResultStatus => {
	switch (reasonCode) {
		case 'Closed':
			return 'Closed';
		case 'Disabled':
			return 'Disabled';
		case 'Unavailable':
			return 'Unavailable';
		case 'WalkOnNotOffered':
			return 'WalkOnNotOffered';
		case 'NotFound':
			return 'NoTripFound';
		default:
			return 'SoldOut';
	}
};

export default function InvalidBulkTripsList({
	bulkBookingErrors,
	remainingTripCount,
	onRemoveAndProceed,
	onChooseDifferentTrips,
	onCancel,
}: InvalidBulkTripsListProps) {
	const intl = useIntl();
	// Every selected trip was refused, so "remove the invalid ones and proceed" would send an empty
	// trip list, which the server rejects outright. The only way forward is a different set of trips.
	const noTripsRemain = remainingTripCount <= 0;
	// Generate data based on the errors for visualization
	const invalidTrips: TripData[] = useMemo(() => {
		return bulkBookingErrors.map(error => {
			const date = new Date(error.departureTime);
			const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

			let errorMessage: string | undefined;
			const warnings = error.warnings || [];
			const reasons = warnings.map(w => w.displayName).filter(Boolean);
			if (reasons.length > 0) {
				errorMessage = `Capacity exceeded for: ${reasons.join(', ')}`;
			} else {
				// The server says why when the trip was disabled, closed or made private since it was
				// picked. Only fall back to the generic line when it has not.
				errorMessage = error.reason || 'Trip is unavailable';
			}

			return {
				id: error.journey.tripId,
				date,
				time,
				status: statusForReason(error.reasonCode),
				errorMessage,
				overbookingWarnings: error.warnings,
			};
		});
	}, [bulkBookingErrors]);

	return (
		<div className="flex flex-col w-full h-full max-w-3xl gap-4 mx-auto">
			<WizardStepHeader
				iconName="event_busy"
				title={
					noTripsRemain
						? intl.formatMessage({
								id: 'booking-wizard__invalid-bulk-trips__none-remain__header',
								defaultMessage: 'No trips are available',
							})
						: intl.formatMessage({
								id: 'booking-wizard__invalid-bulk-trips__header',
								defaultMessage: 'Some trips are unavailable',
							})
				}
				subtitle={
					noTripsRemain
						? intl.formatMessage({
								id: 'booking-wizard__invalid-bulk-trips__none-remain__subtitle',
								defaultMessage:
									'None of the trips you selected are still available, so there is nothing '
									+ 'left to book. Choose a different set of trips to continue.',
							})
						: intl.formatMessage({
								id: 'booking-wizard__invalid-bulk-trips__subtitle',
								defaultMessage:
									'The trips listed below are no longer available. '
									+ 'You can continue with the remaining trips or cancel and start a new bulk booking.',
							})
				}
			/>

			<ListContainer
				title={intl.formatMessage({
					id: 'booking-wizard__invalid-bulk-trips__list__title',
					defaultMessage: 'Unavailable Trips',
				})}
				subtitle={intl.formatMessage(
					{
						id: 'booking-wizard__invalid-bulk-trips__list__subtitle',
						defaultMessage: '{count} trip(s) affected',
					},
					{ count: invalidTrips.length },
				)}
				className="flex-1"
			>
				{invalidTrips.map(trip => (
					<TripResultItem
						key={trip.id}
						date={trip.date}
						time={trip.time}
						status={trip.status}
						errorMessage={trip.errorMessage}
						errorContent={
							trip.overbookingWarnings && trip.overbookingWarnings.length > 0 ? (
								<OverbookingWarningList overbookingWarnings={trip.overbookingWarnings} />
							) : undefined
						}
					/>
				))}
			</ListContainer>

			<div className="flex flex-wrap justify-end gap-4 mt-4">
				{noTripsRemain ? (
					<>
						<Button onClick={onCancel} display={Display.Outline} colors={Colors.Grey} newStyles>
							Cancel bulk booking
						</Button>
						<Button
							onClick={onChooseDifferentTrips}
							display={Display.Solid}
							colors={Colors.Secondary}
							newStyles
						>
							Choose different trips
						</Button>
					</>
				) : (
					<>
						<Button onClick={onCancel} display={Display.Outline} colors={Colors.Grey} newStyles>
							Cancel bulk booking
						</Button>
						<Button onClick={onRemoveAndProceed} display={Display.Solid} colors={Colors.Secondary} newStyles>
							Remove invalid trips and proceed
						</Button>
					</>
				)}
			</div>
		</div>
	);
}
